/** Opaque question id (stable across regulations, see design D-2). */
export type QuestionId = string;

/** Latest answer to one question. */
export interface AnsweredEntry {
  /** Zero-based index of the chosen option; `-1` when unknown or compacted away. */
  lastChoice: number;
  correct: boolean;
  /** Epoch ms supplied by the caller; `0` when unknown or compacted away. */
  at: number;
}

/**
 * "Đánh dấu câu" mark of one question (F8 T-3.5). Removing a bookmark keeps `on: false` (a
 * tombstone) so the sync does not bring it back from another device.
 */
export interface BookmarkEntry {
  on: boolean;
  /** Epoch ms supplied by the caller. */
  at: number;
}

/**
 * Review progress for one license class. Questions are shared between
 * regulations, so progress is not keyed by `regulationId`.
 */
export interface Progress {
  version: 1;
  licenseClass: string;
  /** Latest result per question id. */
  answered: Record<QuestionId, AnsweredEntry>;
  /** Ordered (oldest first), unique ids whose latest answer was wrong. */
  wrong: QuestionId[];
  /** Epoch ms of the last change; `0` for an empty record. */
  updatedAt: number;
  /**
   * Bookmark marks by question id, tombstones included (F8 T-3.5). Absent in records written before
   * bookmarks existed and when there is none.
   */
  bookmarks?: Record<QuestionId, BookmarkEntry>;
}

export type ProgressListener = (licenseClass: string, progress: Progress) => void;

/**
 * Where review progress lives. The UI does not know whether it is local
 * (F2) or synced through the API (F6). All methods are synchronous; a sync
 * adapter keeps a local copy and flushes in the background.
 * Time is injected (`now`, epoch ms): ports never read the clock.
 */
export interface ProgressRepo {
  /** Current progress; an empty record when nothing is stored or data is unusable. */
  load(licenseClass: string): Progress;
  /**
   * Records the latest answer. A wrong answer moves the id to the end of
   * `wrong`; a correct one removes it. Returns the saved progress.
   */
  recordAnswer(
    licenseClass: string,
    questionId: QuestionId,
    choice: number,
    correct: boolean,
    now: number,
  ): Progress;
  /**
   * Sets the bookmark of a question (`on: false` keeps a tombstone). Answers and `wrong` are kept.
   * Returns the saved progress.
   */
  setBookmark(licenseClass: string, questionId: QuestionId, on: boolean, now: number): Progress;
  /** Empties the `wrong` list, keeping `answered`. */
  clearWrong(licenseClass: string, now: number): Progress;
  /** Drops all progress for the class. */
  reset(licenseClass: string): Progress;
  /** Notified after every change made through this repo. Returns unsubscribe. */
  subscribe(listener: ProgressListener): () => void;
}

export function emptyProgress(licenseClass: string): Progress {
  return { version: 1, licenseClass, answered: {}, wrong: [], updatedAt: 0 };
}
